import React from "react";
import useApi from "../useApi";

export type QuestionType = "scale" | "single_option" | "multiple_option";

export interface TransmissionQuestion {
  _id: string;
  questionSequence: number;
  question: string;
  answer: string;
  questionType: QuestionType;
  option: string | string[];
  questionDateInStringFormat: string;
  questionSendTimeStamp?: number;
}

export interface TransmissionResponse {
  msg: string;
  data: TransmissionQuestion[];
  status: "success" | "error";
}

const useFetchTransmissionPdf = (billingCycleId: string) => {
  const [transmissionData, setTransmissionData] =
    React.useState<TransmissionResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const { loading: isLoading, makeApiCall } = useApi({
    url: `/rtm/patient-dashboard/pdf/v3/transmission/${billingCycleId}`,
    method: "GET",
  });

  const fetchTransmissionData = React.useCallback(async () => {
    try {
      const { response, error: apiError } = await makeApiCall<
        undefined,
        TransmissionResponse
      >();

      // Handle API level errors
      if (apiError) {
        const errorMessage =
          apiError.msg || "Failed to fetch transmission data";
        setError(errorMessage);
        setTransmissionData(null);
        return null;
      }

      // If we have a successful response with data
      if (response && response.status === "success") {
        setTransmissionData(response);
        setError(null);
        return response;
      }

      // If we get here, something unexpected happened
      setError("Unexpected response format from server");
      setTransmissionData(null);
      return null;
    } catch (err) {
      // console.error('Error fetching transmission data:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to fetch transmission data";
      setError(errorMessage);
      setTransmissionData(null);
      return null;
    }
  }, [billingCycleId, makeApiCall]);

  return {
    transmissionData,
    fetchTransmissionData,
    isLoading,
    error,
  };
};

export default useFetchTransmissionPdf;
